import React from 'react';
import { useParams } from 'react-router-dom';
import { isNotificationUnread } from '../../../../data/filters/notifications/isNotificationUnread';
import { useI18n } from '../../../../data/hooks/i18nHooks';
import { useEventQAAnswerNotifications } from '../../../../data/hooks/notifications/useEventQAAnswerNotifications/useEventQAAnswerNotifications';
import AttachmentsList from '../../../elements/AttachmentsList/AttachmentsList';
import Link from '../../../elements/Link';
import ListItem from '../../../elements/ListItem/ListItem';
import { ListItemContent } from '../../../elements/ListItem/ListItemContent';
import { ListItemImage } from '../../../elements/ListItem/ListItemImage';
import NotificationsBubble from '../../../elements/NotificationsBubble';
import RightChevron from '../../../elements/RightChevron/RightChevron';
import TimeFromNow from '../../../elements/TimeFromNow';
import { extractUsername } from '../../../elements/UserName/extractUsername';
import QuestionCategoryTag from '../common/QuestionCategoryTag';
import stylesheet from './EventQuestQuestionListItem.module.scss';

/**
 * List item for an event question, showing the asker, content, attachments, answer count
 * and unread answer notifications. Links to the question page unless `href` is falsy.
 *
 * @param {Object} props
 * @param {Object} props.event - Event the question belongs to.
 * @param {Object} props.question - Question to show (asker, content, attachments, answers, date_created, category_id).
 * @param {boolean} [props.showTagsOnList] - Show the question category tag.
 * @param {boolean} [props.trimQuestionContent=true] - Truncate content longer than 256 characters.
 * @param {Function} [props.onImageClicked] - Called when an attachment thumbnail is clicked.
 * @param {string} [props.href] - Link target; defaults to `./question/<question.id>`. Falsy renders a plain
 *   `div` without the chevron.
 * @returns {JSX.Element}
 */
const EventQuestQuestionListItem = (props) => {
    const { event, question, showTagsOnList, trimQuestionContent = true, onImageClicked, href = ['./question', question.id].join('/'), ...otherProps } = props,
        i18n = useI18n(),
        { qcatId } = useParams(),
        highlight = ['mine', 'all'].includes(qcatId) && !!question.category_id,
        unreadNotifications = useEventQAAnswerNotifications(event.id, question.id, { filter: isNotificationUnread });

    return (
        <ListItem className={[highlight ? 'whitebg' : ''].join(' ')} tagName={href ? Link : 'div'} href={href} {...otherProps}>
            <ListItemImage className={stylesheet.image} src={question.asker.image_url} altSrc="img/no_avatar.png" next="next" href={['.', 'participants', question.asker.id, '-'].join('/')} round>
                <NotificationsBubble className={stylesheet.notificationsBubble}>{unreadNotifications.length}</NotificationsBubble>
            </ListItemImage>
            <ListItemContent>
                <p className="text main">{trimQuestionContent && question.content.length > 256 ? question.content.substring(0, 255) + '...' : question.content}</p>
                {/* Keep thumbnail clicks from also triggering the list item link */}
                <AttachmentsList className="" attachments={question.attachments} size={50} srcSize={64} onImageClicked={onImageClicked} />
                <p className={[stylesheet.name, 'text'].join(' ')}>{[extractUsername(question.asker), question.asker.position, question.asker.company].filter(Boolean).join(', ')}</p>
                <p className="text">
                    <TimeFromNow>{question.date_created}</TimeFromNow>, {question.answers.count}&nbsp; {i18n['label.answers']}
                </p>
                {showTagsOnList ? <QuestionCategoryTag event={event} question={question} /> : null}
            </ListItemContent>
            {href ? <RightChevron /> : null}
        </ListItem>
    );
};

export default EventQuestQuestionListItem;
